import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { API_BASE_URL } from '../../config/api';

interface SentryIssue {
  id: string;
  shortId: string;
  title: string;
  permalink: string | null;
  count: number;
  userCount: number;
  level: string;
  project: string;
  lastSeen: string | null;
}

interface SentryProject {
  name: string;
  count: number;
}

interface SentryDay {
  date: string;
  events: number;
}

interface SentryData {
  days: number;
  configured: boolean;
  error: string | null;
  organization: string | null;
  unresolved: number;
  newIssues: number;
  events: number;
  users: number;
  projects: SentryProject[];
  issues: SentryIssue[];
  series: SentryDay[];
}

const RANGES = [
  { value: 7, label: '7 days' },
  { value: 14, label: '14 days' },
  { value: 30, label: '30 days' },
];

const PROJECT_COLORS = ['#2563eb', '#7c3aed', '#0d9488', '#ea580c', '#db2777', '#16a34a', '#f59e0b', '#0369a1'];

const levelColor = (level: string) => {
  if (level === 'fatal') return '#7f1d1d';
  if (level === 'error') return '#dc2626';
  if (level === 'warning') return '#d97706';
  if (level === 'info') return '#2563eb';
  return '#6b7280';
};

const formatDay = (value: string) => {
  const date = new Date(`${value}T00:00:00Z`);
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
};

const StoreDashboardSentryView: React.FC = () => {
  const [days, setDays] = useState(14);
  const [data, setData] = useState<SentryData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/store-dashboard/sentry`, {
        params: { days },
        withCredentials: true,
      });
      setData(response.data);
      setError(null);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load Sentry');
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div className="store-dash">
      <div className="store-dash-toolbar">
        <div>
          <div className="store-dash-kicker">{data?.organization ? `Sentry · ${data.organization}` : 'Errors'}</div>
          <h1>Sentry</h1>
        </div>
        <div className="store-dash-ranges">
          {RANGES.map((range) => (
            <button
              key={range.value}
              type="button"
              className={`store-dash-range${days === range.value ? ' is-active' : ''}`}
              onClick={() => setDays(range.value)}
            >
              {range.label}
            </button>
          ))}
        </div>
      </div>

      {data && !data.configured && (
        <p className="store-dash-hint">
          Connect Sentry on the <Link to="/apis">APIs</Link> page to load error metrics.
        </p>
      )}

      {loading ? (
        <div className="d-flex justify-content-center py-5">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      ) : error || data?.error ? (
        <div className="alert alert-danger">{error || data?.error}</div>
      ) : (
        <>
          <div className="store-dash-cards">
            <div className="store-dash-card">
              <span>Unresolved</span>
              <strong>{(data?.unresolved || 0).toLocaleString()}</strong>
            </div>
            <div className="store-dash-card">
              <span>New</span>
              <strong>{(data?.newIssues || 0).toLocaleString()}</strong>
            </div>
            <div className="store-dash-card">
              <span>Events</span>
              <strong>{(data?.events || 0).toLocaleString()}</strong>
            </div>
            <div className="store-dash-card">
              <span>Users affected</span>
              <strong>{(data?.users || 0).toLocaleString()}</strong>
            </div>
          </div>

          <section className="store-dash-chart">
            <h2>Errors over time</h2>
            {(data?.series || []).length === 0 ? (
              <p className="store-dash-hint">No error events in this range.</p>
            ) : (
              <ResponsiveContainer width="100%" height={280}>
                <LineChart data={data?.series || []}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="date" tickFormatter={formatDay} minTickGap={24} />
                  <YAxis allowDecimals={false} />
                  <Tooltip labelFormatter={formatDay} />
                  <Line type="monotone" dataKey="events" name="Events" stroke="#dc2626" strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            )}
          </section>

          <div className="store-dash-fleet-grid">
            <section className="store-dash-chart">
              <h2>Top issues</h2>
              {(data?.issues || []).length === 0 ? (
                <p className="store-dash-hint">No unresolved issues.</p>
              ) : (
                <div className="store-dash-legend">
                  {data?.issues.map((issue) => (
                    <div key={issue.id} className="store-dash-legend-item">
                      <i style={{ background: levelColor(issue.level) }} />
                      <span className="store-dash-project">
                        {issue.permalink ? (
                          <a href={issue.permalink} target="_blank" rel="noreferrer">{issue.title}</a>
                        ) : issue.title}
                        <small>{issue.shortId} · {issue.project}</small>
                      </span>
                      <em>{issue.count.toLocaleString()}</em>
                    </div>
                  ))}
                </div>
              )}
            </section>
            <section className="store-dash-chart">
              <h2>Projects</h2>
              {(data?.projects || []).length === 0 ? (
                <p className="store-dash-hint">No projects in this issue list.</p>
              ) : (
                <div className="store-dash-legend">
                  {data?.projects.map((project, index) => (
                    <div key={project.name} className="store-dash-legend-item">
                      <i style={{ background: PROJECT_COLORS[index % PROJECT_COLORS.length] }} />
                      <span>{project.name}</span>
                      <em>{project.count.toLocaleString()}</em>
                    </div>
                  ))}
                </div>
              )}
            </section>
          </div>
        </>
      )}
    </div>
  );
};

export default StoreDashboardSentryView;
